import React, { useState, useEffect, useCallback } from 'react';
import NoteEditor from './NoteEditor';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  StickyNote,
  Plus,
  Pin,
  MoreVertical,
  Pencil,
  Trash2,
  Lock,
  Users,
  Globe,
  Tag,
  Calendar,
} from 'lucide-react';
import { cn, formatDate } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';

export default function ProjectNotesTab({ project }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [deleteConfirmNote, setDeleteConfirmNote] = useState(null);

  const loadNotes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/${project.id}/notes`);
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to load project notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleCreateNote = async (noteData) => {
    try {
      // Pre-fill with project link
      const data = {
        ...noteData,
        linked_resource_type: 'project',
        linked_resource_id: project.id,
        linked_resource_title: project.name,
      };
      await api.post('/notes', data);
      toast.success('Note created');
      setIsCreateDialogOpen(false);
      loadNotes();
    } catch (error) {
      console.error('Failed to create note:', error);
      toast.error('Failed to create note');
    }
  };

  const handleUpdateNote = async (noteData) => {
    try {
      await api.put(`/notes/${editingNote.id}`, noteData);
      toast.success('Note updated');
      setEditingNote(null);
      loadNotes();
    } catch (error) {
      console.error('Failed to update note:', error);
      toast.error('Failed to update note');
    }
  };

  const handleDeleteNote = async () => {
    if (!deleteConfirmNote) return;
    try {
      await api.delete(`/notes/${deleteConfirmNote.id}`);
      toast.success('Note deleted');
      setDeleteConfirmNote(null);
      loadNotes();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  };

  const handleTogglePin = async (note) => {
    try {
      await api.put(`/notes/${note.id}`, { pinned: !note.pinned });
      toast.success(note.pinned ? 'Note unpinned' : 'Note pinned');
      loadNotes();
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      toast.error('Failed to update note');
    }
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'private':
        return Lock;
      case 'shared':
        return Users;
      case 'org':
        return Globe;
      default:
        return Lock;
    }
  };

  return (
    <div className="space-y-6" data-testid="project-notes-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Project Notes</h2>
          <p className="text-sm text-muted-foreground">
            Notes and observations about {project.name}
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="create-project-note-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Note
        </Button>
      </div>

      {/* Notes List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-3" />
              <div className="h-3 bg-muted rounded w-full mb-2" />
              <div className="h-3 bg-muted rounded w-2/3" />
            </Card>
          ))}
        </div>
      ) : notes.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg">
          <StickyNote className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">
            No notes for this project yet
          </p>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add First Note
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {notes.map(note => {
            const VisibilityIcon = getVisibilityIcon(note.visibility);
            return (
              <Card
                key={note.id}
                className={cn(
                  "p-4 cursor-pointer hover:shadow-md transition-shadow",
                  note.pinned && "ring-2 ring-primary/20",
                  note.color && "border-l-4"
                )}
                style={note.color ? { borderLeftColor: note.color } : undefined}
                onClick={() => setEditingNote(note)}
                data-testid={`project-note-${note.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {note.pinned && (
                      <Pin className="h-4 w-4 text-primary" />
                    )}
                    <VisibilityIcon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        setEditingNote(note);
                      }}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        handleTogglePin(note);
                      }}>
                        <Pin className="h-4 w-4 mr-2" />
                        {note.pinned ? 'Unpin' : 'Pin'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmNote(note);
                        }}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {note.title && (
                  <h3 className="font-semibold mb-2 line-clamp-1">{note.title}</h3>
                )}

                <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                  {note.content}
                </p>

                {/* Tags */}
                {note.tags && note.tags.length > 0 && (
                  <div className="flex gap-1 flex-wrap mb-3">
                    {note.tags.slice(0, 3).map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {note.tags.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{note.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-auto pt-2 border-t">
                  <Calendar className="h-3 w-3" />
                  {formatDate(note.created_at)}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Note Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Note to {project.name}</DialogTitle>
          </DialogHeader>
          <NoteEditor
            onSave={handleCreateNote}
            onCancel={() => setIsCreateDialogOpen(false)}
            defaultLinkedResource={{
              type: 'project',
              id: project.id,
              title: project.name,
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Note Dialog */}
      <Dialog open={!!editingNote} onOpenChange={(open) => !open && setEditingNote(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
          </DialogHeader>
          {editingNote && (
            <NoteEditor
              note={editingNote}
              onSave={handleUpdateNote}
              onCancel={() => setEditingNote(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmNote} onOpenChange={(open) => !open && setDeleteConfirmNote(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this note. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteNote}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
